import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Loader from '@/components/Atoms/Loader/Loader'

import {
  DEFAULT_COLUMN_WIDTH,
  getColumnsMinWidthPx,
} from '../lib/stockImportsTableWidth'
import cn from './StockImportsVirtualTable.module.scss'

export type StockImportsTableColumn<T extends RowData> = ColumnDef<
  T,
  unknown
> & {
  width?: string
}

interface Props<T extends RowData> {
  data: T[]
  columns: StockImportsTableColumn<T>[]
  rowKey: (record: T, index: number) => string
  emptyText: string
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  minTableWidth?: string
  estimateRowHeight?: number
  /** Minimum height in px (used as-is when `fillHeight` is false, or as a floor when it's true). */
  height?: number | string
  /** When true, the table measures its position and stretches to the bottom of the viewport instead of using `height` as a fixed/max value. */
  fillHeight?: boolean
  /** Gap left between the bottom of the table and the bottom of the viewport when `fillHeight` is set. */
  fillHeightBottomGap?: number
  organisationSelected?: boolean
  className?: string
  rowClassName?: (record: T, index: number) => string | undefined
  compact?: boolean
  onRowClick?: (record: T) => void
}

const ROW_CLICK_DELAY_MS = 250
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], [role="checkbox"], .ant-select, .ant-tag-close-icon'

const LOAD_MORE_THRESHOLD_ROWS = 4

const hasTextSelection = () =>
  (window.getSelection()?.toString() ?? '').trim().length > 0

const releaseHoveredPopups = (scrollElement: HTMLElement) => {
  const hoveredElements = scrollElement.querySelectorAll(':hover')
  const hoveredElement = hoveredElements[hoveredElements.length - 1]
  if (!hoveredElement) return

  const eventInit = {
    bubbles: true,
    relatedTarget: scrollElement.parentElement,
  }
  if (typeof PointerEvent === 'function')
    hoveredElement.dispatchEvent(new PointerEvent('pointerout', eventInit))
  hoveredElement.dispatchEvent(new MouseEvent('mouseout', eventInit))
}

function StockImportsVirtualTable<T extends RowData>({
  data,
  columns,
  rowKey,
  emptyText,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  minTableWidth = '920px',
  estimateRowHeight = 56,
  height = 320,
  fillHeight = false,
  fillHeightBottomGap = 54,
  organisationSelected = false,
  className,
  rowClassName,
  compact = false,
  onRowClick,
}: Props<T>) {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const rowClickTimerRef = useRef<number>()
  const minHeightPx = typeof height === 'number' ? height : 240
  const [measuredHeightPx, setMeasuredHeightPx] = useState(minHeightPx)
  const tableHeight = fillHeight
    ? `${measuredHeightPx}px`
    : typeof height === 'number'
      ? `${height}px`
      : height

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const rows = table.getRowModel().rows
  const gridTemplateColumns = useMemo(
    () =>
      columns.map((column) => column.width ?? DEFAULT_COLUMN_WIDTH).join(' '),
    [columns]
  )
  const columnsMinWidthPx = getColumnsMinWidthPx(
    columns.map((column) => column.width)
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateRowHeight,
    measureElement: (element) => element.getBoundingClientRect().height,
    // Ключ по содержимому, а не по индексу: без него после сброса данных
    // (например, при поиске в MissingSkuModal) виртуализатор какое-то время
    // применял бы к новой строке на том же индексе высоту, измеренную
    // для прежнего содержимого, и строки наезжали бы друг на друга.
    getItemKey: (index) => rowKey(rows[index]?.original as T, index),
    overscan: 14,
  })
  const virtualItems = rowVirtualizer.getVirtualItems()

  useEffect(() => {
    const element = parentRef.current
    if (!element) return

    const handleScroll = () => releaseHoveredPopups(element)
    element.addEventListener('scroll', handleScroll, { passive: true })
    return () => element.removeEventListener('scroll', handleScroll)
  }, [loading, rows.length])

  useEffect(() => {
    if (!onLoadMore || !hasMore || loading || loadingMore) return
    const element = parentRef.current
    if (!element) return

    const loadMoreNearBottom = () => {
      const distanceToBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight
      if (distanceToBottom <= estimateRowHeight * LOAD_MORE_THRESHOLD_ROWS)
        onLoadMore()
    }

    const frame = window.requestAnimationFrame(loadMoreNearBottom)
    element.addEventListener('scroll', loadMoreNearBottom, { passive: true })
    return () => {
      window.cancelAnimationFrame(frame)
      element.removeEventListener('scroll', loadMoreNearBottom)
    }
  }, [
    estimateRowHeight,
    hasMore,
    loading,
    loadingMore,
    onLoadMore,
    rows.length,
  ])

  useLayoutEffect(() => {
    if (!fillHeight) return
    const element = parentRef.current
    if (!element) return

    const recalc = () => {
      const top = element.getBoundingClientRect().top
      const available = window.innerHeight - top - fillHeightBottomGap
      setMeasuredHeightPx(Math.max(minHeightPx, Math.floor(available)))
    }

    recalc()
    window.addEventListener('resize', recalc)
    const resizeObserver = new ResizeObserver(recalc)
    resizeObserver.observe(document.body)

    return () => {
      window.removeEventListener('resize', recalc)
      resizeObserver.disconnect()
    }
  }, [fillHeight, minHeightPx, fillHeightBottomGap, loading, data.length])

  useEffect(() => () => window.clearTimeout(rowClickTimerRef.current), [])

  const handleRowClick = (event: MouseEvent<HTMLDivElement>, record: T) => {
    window.clearTimeout(rowClickTimerRef.current)
    if (!onRowClick || event.detail > 1) return
    if (!event.currentTarget.contains(event.target as Node)) return
    if ((event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return
    if (hasTextSelection()) return

    rowClickTimerRef.current = window.setTimeout(() => {
      if (hasTextSelection()) return
      onRowClick(record)
    }, ROW_CLICK_DELAY_MS)
  }

  const renderHeaderCells = () =>
    table.getHeaderGroups().map((headerGroup) =>
      headerGroup.headers.map((header) => (
        <div key={header.id} className={cn.headerCell}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      ))
    )

  if (loading && data.length === 0) {
    return (
      <div
        ref={parentRef}
        className={cn.stateBox}
        style={fillHeight ? { height: tableHeight } : undefined}
      >
        <Loader size={24} />
      </div>
    )
  }

  if (!loading && data.length === 0) {
    return (
      <div
        ref={parentRef}
        className={cn.stateBox}
        style={fillHeight ? { height: tableHeight } : undefined}
      >
        <span>{emptyText}</span>
      </div>
    )
  }

  return (
    <div
      ref={parentRef}
      className={[
        cn.tableWrapper,
        organisationSelected ? cn.organisationSelected : '',
        compact ? cn.compact : '',
        className ?? '',
      ].join(' ')}
      style={fillHeight ? { height: tableHeight } : { maxHeight: tableHeight }}
    >
      <div
        className={cn.tableInner}
        style={{
          minWidth: `max(100%, ${minTableWidth}, ${columnsMinWidthPx}px)`,
        }}
      >
        <div className={cn.virtualHeader} style={{ gridTemplateColumns }}>
          {renderHeaderCells()}
        </div>

        <div
          className={cn.virtualBody}
          style={{
            height: rowVirtualizer.getTotalSize(),
          }}
        >
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index]
            if (!row) return null

            return (
              <div
                key={rowKey(row.original, virtualRow.index)}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                className={[
                  cn.virtualRow,
                  onRowClick ? cn.clickableRow : '',
                  rowClassName?.(row.original, virtualRow.index) ?? '',
                ].join(' ')}
                onClick={
                  onRowClick
                    ? (event) => handleRowClick(event, row.original)
                    : undefined
                }
                style={{
                  gridTemplateColumns,
                  minHeight: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className={cn.virtualCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            )
          })}
        </div>

        {(hasMore || loadingMore) && (
          <div className={cn.loadMoreAnchor}>
            {loadingMore ? <Loader size={18} /> : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default StockImportsVirtualTable
